// src/components/HRComponents/PolicyList.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Search, Eye, Edit2, Filter, X, FileText, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';

import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import { UserListSkeleton } from '../../components/SkeletonComponents/UserListSkeleton';
import SelectDropdown from '../../customStyles/SelectDropdown';
import ModernTable from '../../components/ui/ModernTable';
import { formatDateTime } from '../../helpers/formatDateTime';
import PolicyStatusBadge from '../../components/HRComponents/PolicyStatusBadge';

const DOCUMENT_TYPE_OPTIONS = [
  { label: 'All Types', value: '' },
  { label: 'Letter', value: 'letter' },
  { label: 'Policy', value: 'policy' },
];

// FIX: Added 'Approved' option to the dropdown filter list [1]
const STATUS_OPTIONS = [
  { label: 'All Status', value: '' },
  { label: 'Draft', value: 'draft' },
  { label: 'Pending Approval', value: 'pending_approval' },
  { label: 'Approved', value: 'approved' }, 
  { label: 'Published', value: 'published' },
  { label: 'Archived', value: 'archived' },
];

const PolicyList = () => {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);

  const [loadingStates, setLoadingStates] = useState({ policies: true, categories: true });
  const isLoading = useMemo(() => Object.values(loadingStates).some((l) => l), [loadingStates]);
  const updateLoadingState = (key, value) => setLoadingStates((prev) => ({ ...prev, [key]: value }));

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [showFilters, setShowFilters] = useState(false);
  const [documentTypeFilter, setDocumentTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Fetch policy categories from the backend
  const fetchCategories = useCallback(async () => {
    try {
      updateLoadingState('categories', true);
      const res = await api.get(SummaryApi.getPolicyCategories.url);
      if (res.data.success) setCategories(res.data.data || []);
    } catch (error) {
      console.error('Category fetch error:', error);
    } finally {
      updateLoadingState('categories', false);
    }
  }, []);

  // Fetch paginated and filtered policies from the backend
  const fetchPolicies = useCallback(async () => {
    updateLoadingState('policies', true);
    try {
      const res = await api.get(SummaryApi.getAllPolicies.url, {
        params: {
          q: searchTerm || undefined,
          documentType: documentTypeFilter || undefined,
          status: statusFilter || undefined,
          category: categoryFilter || undefined,
          page: currentPage,
          limit: itemsPerPage === 'All' ? 1000 : itemsPerPage,
        },
      });
      if (res.data.success) {
        setPolicies(res.data.data || []);
        setTotal(res.data.total || 0);
      }
    } catch (error) {
      toast.error('Failed to load policies & letters');
    } finally {
      updateLoadingState('policies', false);
    }
  }, [searchTerm, documentTypeFilter, statusFilter, categoryFilter, currentPage, itemsPerPage]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  const handleAdd = () => navigate('create');
  const handleView = (row) => navigate(`view/${row._id}`);

  // FIX: Lock direct editing of any document that is not in raw 'draft' state [1]
  // (Prevents modifications during approval, or after approved/publishing states)
  const handleEdit = (row) => {
    if (row.status !== 'draft') {
      return toast.warning('Only draft documents can be edited directly. Approved or Published documents are locked.');
    }
    navigate(`edit/${row._id}`);
  };

  // Prevent downloading if the document is not published yet
  // const handleDownload = async (row) => {
  //   if (row.status !== 'published') {
  //     return toast.warning('This document is not published yet. Please publish it to download.');
  //   }

  //   try {
  //     const res = await api.get(SummaryApi.downloadPolicy.url(row._id), { responseType: 'blob' });
  //     const url = window.URL.createObjectURL(new Blob([res.data]));
  //     const link = document.createElement('a');
  //     link.href = url;
  //     link.setAttribute('download', row.currentVersionId?.fileName || `${row.title}.pdf`);
  //     document.body.appendChild(link);
  //     link.click();
  //     link.remove();
  //   } catch (error) {
  //     toast.error('Download failed');
  //   }
  // };

  // Prevent sending reminders on draft/archived/pending documents
  const handleRemind = async (row) => {
    if (row.status !== 'published') {
      return toast.warning('Reminders can only be sent for published documents.');
    }

    try {
      await api.post(SummaryApi.bulkRemindPolicy.url, { policyDocumentId: row._id });
      toast.success('Reminders sent to pending employees');
    } catch (error) {
      toast.error('Failed to send reminders');
    }
  };

  // Define table columns
  const columns = [
    {
      label: 'Document',
      key: 'title',
      render: (row) => (
        <div className="flex items-center min-w-[220px]">
          <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
            <FileText className="h-4 w-4 text-red-500" />
          </div>
          <div className="ml-3">
            <div className="text-sm font-semibold text-gray-900">{row.title}</div>
            <div className="text-xs text-gray-500">{row.policyCode}</div>
          </div>
        </div>
      ),
    },
    {
      label: 'Type',
      key: 'documentType',
      render: (row) => (
        <span className="text-sm capitalize text-gray-700">{row.documentType}</span>
      ),
    },
    {
      label: 'Category',
      key: 'category',
      render: (row) => <span className="text-sm text-gray-700">{row.category?.name || '-'}</span>,
    },
    {
      label: 'Status',
      key: 'status',
      render: (row) => <PolicyStatusBadge status={row.status} />,
    },
    {
      label: 'Version',
      key: 'currentVersionNumber',
      render: (row) => <span className="text-sm text-gray-700">v{row.currentVersionNumber || 0}</span>,
    },
    {
      label: 'Published',
      key: 'publishedAt',
      render: (row) => <div className="text-sm">{row.publishedAt ? formatDateTime(row.publishedAt, false) : '-'}</div>,
    },
  ];

  // Define table actions
  const tableActions = [
    { label: 'View Details', icon: <Eye className="h-4 w-4" />, onClick: handleView },
    { label: 'Edit', icon: <Edit2 className="h-4 w-4" />, onClick: handleEdit },
    // { label: 'Download', icon: <Download className="h-4 w-4" />, onClick: handleDownload },
    { label: 'Send Reminders', icon: <Send className="h-4 w-4" />, onClick: handleRemind },
  ];

  if (isLoading) return <UserListSkeleton />;

  return (
    <div className="p-4">
      {/* Header section */}
      <div className="mb-6 flex flex-row justify-between items-center gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="rounded-lg bg-red-50 border border-red-100 hover:border-primary text-textPrimary hover:text-primary p-1.5 sm:p-2 transition-colors"
          >
            <FaArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 whitespace-nowrap">
            HR Letters<span className="hidden sm:inline"> & Policies</span>
          </h1>
        </div>

        <button
          onClick={handleAdd}
          className="flex items-center justify-center gap-2 bg-primary text-white p-2 sm:px-4 sm:py-2 rounded-lg hover:shadow-lg transition-all flex-shrink-0"
        >
          <Plus className="h-5 w-5" />
          <span className="hidden sm:block text-sm sm:text-base font-medium">New Document</span>
        </button>
      </div>

      {/* Search and Filters bar */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by title..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-xl transition-colors ${showFilters ? 'bg-red-50 border-red-300 text-red-700' : 'bg-white border-gray-300'}`}
          >
            <Filter className="h-5 w-5" /> Filters
          </button>
        </div>

        {/* Collapsible Filters section */}
        {showFilters && (
          <div className="bg-white p-4 rounded-xl shadow-sm border grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-in fade-in slide-in-from-top-2">
            <SelectDropdown label="Type" options={DOCUMENT_TYPE_OPTIONS} value={documentTypeFilter} onChange={(v) => { setDocumentTypeFilter(v); setCurrentPage(1); }} valueKey="value" labelKey="label" />
            <SelectDropdown label="Status" options={STATUS_OPTIONS} value={statusFilter} onChange={(v) => { setStatusFilter(v); setCurrentPage(1); }} valueKey="value" labelKey="label" />
            <SelectDropdown label="Category" options={[{ name: 'All Categories', _id: '' }, ...categories]} value={categoryFilter} onChange={(v) => { setCategoryFilter(v); setCurrentPage(1); }} valueKey="_id" labelKey="name" searchable />
            <button
              onClick={() => { setDocumentTypeFilter(''); setStatusFilter(''); setCategoryFilter(''); setSearchTerm(''); setCurrentPage(1); }}
              className="text-red-600 text-sm font-medium flex items-center gap-1 hover:underline"
            >
              <X className="h-4 w-4" /> Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Main Table component */}
      <ModernTable
        columns={columns}
        data={policies}
        actions={tableActions}
        selectable={false}
        stickyColumns={1}
        pagination={{
          totalItems: total,
          itemsPerPage,
          currentPage,
          onPageChange: setCurrentPage,
          onItemsPerPageChange: (val) => { setItemsPerPage(val); setCurrentPage(1); },
        }}
      />

      {/* Empty State display */}
      {!isLoading && policies.length === 0 && (
        <div className="bg-white rounded-xl border p-12 text-center mt-4">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No letters or policies found</h3>
          <p className="text-gray-500">Try adjusting your filters, or create a new document.</p>
        </div>
      )}
    </div>
  );
};

export default PolicyList;